// Real 2008 chibi paper-doll avatar. For the current pose + facing it reads the
// decoded per-(pose,direction) LAYOUT from the chibi manifest, the exact draw
// order and pixel offset of every layer, recovered from the original SWF
// placement matrices, and composites the player's chosen variant/colour per
// slot. Because the layout is per-direction, alignment and z-order (e.g. back
// hair showing from behind) are correct at every angle with no hand-tuning.
//
// Directions: 01 front(S), 02 front¾(SW), 03 side(W), 04 back¾(NW), 05 back(N).
// The right half (E/SE/NE) is the horizontal mirror of the left. Walk cycles up
// to 4 frames; slots with fewer frames clamp. Idle holds frame 1.

import { Container, Sprite, Texture, Assets } from "pixi.js";
import { type ChibiManifest, type ChibiGender, slotCategory } from "@ygoevo/yo2-data";
import type { Facing, AvatarSelection } from "@ygoevo/world-protocol";
import { asset } from "../data.ts";

/** Per-slot variant + colour the player has chosen. variant "00"/absent = skip. */
export type ChibiSelection = Record<string, { variant: string; color: number }>;

export interface ChibiOptions {
  manifest: ChibiManifest;
  gender: "cm" | "cw";
  selection: ChibiSelection;
  /** On-screen height in px for the 128-tall sprite. */
  displayHeight: number;
}

/** A clean default duelist loadout (front + back hair, basic duel disk, no accessories). */
export const DEFAULT_CHIBI: ChibiSelection = {
  sh: { variant: "01", color: 1 },
  h2: { variant: "01", color: 1 },
  bo: { variant: "01", color: 1 },
  dc: { variant: "01", color: 1 },
  mo: { variant: "01", color: 1 },
  ey: { variant: "01", color: 1 },
  h1: { variant: "01", color: 1 },
  dd: { variant: "01", color: 1 }, // basic duel disk (DEFAULT_DUELDISK_SKU) so the fallback wields one too
};

/** Resolve a renderable chibi look from a stored avatar (falls back to default).
 *  Shadow is always forced on so the sprite never floats. */
export function chibiFrom(
  avatar?: Pick<AvatarSelection, "chibi"> | null,
): { gender: "cm" | "cw"; selection: ChibiSelection } {
  if (avatar?.chibi) {
    const selection: ChibiSelection = { sh: { variant: "01", color: 1 }, ...avatar.chibi.slots };
    // Everyone wields a duel disk: if no disk slot (dd/da) is worn, equip the basic one so a duelist
    // is never empty-handed (matches DEFAULT_DUELDISK_SKU = "dd_01_c01"). The chibi map ignores it (no map
    // art for the disk); the enlarged bust composites it at the top layer.
    const wearsDisk = (s?: { variant: string }) => !!s && s.variant !== "00";
    if (!wearsDisk(selection.dd) && !wearsDisk(selection.da)) selection.dd = { variant: "01", color: 1 };
    return { gender: avatar.chibi.gender, selection };
  }
  return { gender: "cm", selection: DEFAULT_CHIBI };
}

interface LayoutEntry { slot: string; x: number; y: number; seq: Array<[string, number]> }
interface SlotSprite { slot: string; sprite: Sprite; seq: Array<[string, number]> }

// Our 8-way facing → source direction index + horizontal mirror. Five directions
// are drawn (01 S, 02 SW, 03 W, 04 NW, 05 N); the right-hand trio (SE/E/NE) is the
// horizontal mirror of SW/W/NW. Dirs 01/05 are symmetric so never mirrored.
const FACING_DIR: Record<Facing, { dir: number; mirror: boolean }> = {
  s: { dir: 1, mirror: false },
  sw: { dir: 2, mirror: false },
  w: { dir: 3, mirror: false },
  nw: { dir: 4, mirror: false },
  n: { dir: 5, mirror: false },
  ne: { dir: 4, mirror: true },
  e: { dir: 3, mirror: true },
  se: { dir: 2, mirror: true },
};

export class ChibiAvatar {
  readonly root = new Container();
  /** Inner container we mirror (pivot at feet-centre) for left/right facing. */
  private body = new Container();
  private opts: ChibiOptions;
  private gender: ChibiGender;
  private layouts: Record<string, Record<number, LayoutEntry[]>>;
  private slots: SlotSprite[] = [];
  private facing: Facing = "s";
  private layoutKey = ""; // `${pose}:${dir}` currently built
  private animTimer = 0;
  private frame = 1;
  /** Length of the current pose's animation cycle (max slot seq length). */
  private cycleLen = 1;
  /** Bumped per rebuild so a slow texture load can't clobber a newer pose. */
  private rebuildGen = 0;
  walking = false;
  /** In a duel: hold the front-facing "disk raised" stance (pose d2 — a single
   *  static frame, no deploy animation) so onlookers see at a glance that this
   *  player is dueling without a busy looping animation. */
  private duelingState = false;

  constructor(opts: ChibiOptions) {
    this.opts = opts;
    this.gender = opts.manifest.genders[opts.gender]!;
    this.layouts = (opts.manifest.layouts?.[opts.gender] ?? {}) as typeof this.layouts;
    this.body.pivot.set(64, 112); // feet-centre, position + mirror pivot (x re-anchored per rebuild from the shadow)
    this.root.addChild(this.body);
    this.root.scale.set(opts.displayHeight / 128);
  }

  private get pose(): string {
    return this.duelingState ? "d2" : this.walking ? "w" : "n";
  }

  /** Source direction for the current state. The duel stance is front-only (dir 1),
   *  and never mirrored, so a dueling avatar always faces the camera. */
  private get curDir(): number {
    return this.duelingState ? 1 : FACING_DIR[this.facing].dir;
  }
  private get curMirror(): boolean {
    return this.duelingState ? false : FACING_DIR[this.facing].mirror;
  }

  /** Toggle the duel stance (front-facing, disk raised). Forces a rebuild so the
   *  pose + disk appear/disappear immediately rather than on the next pose change. */
  setDueling(d: boolean): void {
    if (d === this.duelingState) return;
    this.duelingState = d;
    this.frame = 1;
    this.layoutKey = ""; // force rebuild
    void this.rebuild();
  }

  /** The layout for the current pose+dir, with sensible fallbacks. */
  private currentLayout(dir: number): LayoutEntry[] {
    const byDir = this.layouts[this.pose] ?? this.layouts["n"] ?? {};
    return this.withCostume(byDir[dir] ?? byDir[1] ?? []);
  }

  /** The 2008 base paper-doll SWF never placed the optional full-costume overlay
   *  (`ac`) on stage, so it's missing from every decoded layout, only the
   *  regular clothes slot (`dc`) is. But `ac` ships identical per-direction art
   *  (same poses/frames) and is meant to REPLACE the clothes, so without a slot
   *  to draw into a chosen costume silently vanished on the chibi while outfits
   *  rendered fine. Synthesize `ac` by cloning `dc`'s placement and REPLACING it
   *  (they're mutually exclusive — a costume replaces the outfit; a stale/foreign
   *  look carrying both must not stack). Only injected when a costume is selected. */
  private withCostume(layout: LayoutEntry[]): LayoutEntry[] {
    const sel = this.opts.selection["ac"];
    if (!sel || sel.variant === "00") return layout;
    if (layout.some((e) => e.slot === "ac")) return layout; // already present
    const dc = layout.find((e) => e.slot === "dc");
    if (!dc) return layout;
    const out = layout.slice();
    // ac REPLACES dc (takes its placement + z-order) — a look carrying both shouldn't stack them.
    out.splice(out.indexOf(dc), 1, { slot: "ac", x: dc.x, y: dc.y, seq: dc.seq });
    return out;
  }

  async build(): Promise<void> {
    await this.rebuild();
  }

  /** (Re)create the slot sprites for the current pose+direction layout.
   *  The old sprites stay on screen until the new pose's textures have loaded,
   *  then we swap in one frame, otherwise the avatar blanks out for the frames
   *  the new textures take to fetch (the idle→walk flicker on move start). */
  private async rebuild(): Promise<void> {
    const gen = ++this.rebuildGen;
    const dir = this.curDir;
    const mirror = this.curMirror;
    this.layoutKey = `${this.pose}:${dir}`;
    const layout = this.currentLayout(dir);
    const cycleLen = Math.max(1, ...layout.map((e) => e.seq?.length ?? 1));
    const newSlots: SlotSprite[] = [];
    const loads: Promise<void>[] = [];
    for (const entry of layout) {
      // Shadow is always drawn; the duel disk is always drawn while dueling (there's a
      // single chibi disk art, so it shows regardless of which bust disk is worn); other
      // slots only if the player selected them.
      const sel = this.effectiveSel(entry.slot);
      const alwaysOn = entry.slot === "sh" || (this.duelingState && entry.slot === "dd");
      if (!alwaysOn && (!sel || sel.variant === "00")) continue;
      const sprite = new Sprite();
      sprite.position.set(entry.x, entry.y);
      const s: SlotSprite = { slot: entry.slot, sprite, seq: entry.seq ?? [["n", 1]] };
      newSlots.push(s);
      loads.push(this.applyTexture(s, dir));
    }
    await Promise.all(loads);
    // A newer rebuild (e.g. another facing/pose change) superseded us mid-load,
    // drop this stale result so it can't overwrite the current sprites.
    if (gen !== this.rebuildGen) {
      for (const s of newSlots) s.sprite.destroy();
      return;
    }
    // Re-anchor the body's horizontal pivot to the chibi's true standing centre.
    // The decoded SWF layouts don't centre the figure in the 128px frame (the male
    // sits ~18px left of frame-centre, the female ~10px right), so a fixed pivot.x
    // of 64 left every avatar off its own name label / click box (anchored at x=0)
    // and made the figure jump sideways when mirrored for E/W facing. The ground
    // shadow is drawn dead under the standing point and its art is symmetric, so
    // its sprite centre is the reliable horizontal anchor: pivot there and the head,
    // label and hit area all line up, and a mirror becomes a true in-place flip.
    // (pivot.y stays on the feet line so Y-sort is unaffected.)
    const sh = newSlots.find((s) => s.slot === "sh");
    if (sh && sh.sprite.texture.width > 1) this.body.pivot.x = sh.sprite.x + sh.sprite.texture.width / 2;

    // Atomic swap: textures are ready, so removing the old sprites here leaves no
    // empty frame.
    const old = this.body.removeChildren();
    this.body.scale.x = mirror ? -1 : 1;
    this.cycleLen = cycleLen;
    this.slots = newSlots;
    for (const s of newSlots) this.body.addChild(s.sprite);
    for (const c of old) c.destroy();
  }

  /** Back hair (h2) follows the front-hair (h1) 'do — it's a layer of the same style,
   *  not a separate pick (the customizer never sets one without the other). Derive it
   *  from h1 so a look that only stored h1 (a default or pre-fan-out legacy save) still
   *  shows its back hair, but only when the chibi actually ships that variant's back-hair
   *  art (else skip — a back-hairless style like a buzzcut stays bald, correctly). */
  private effectiveSel(slot: string): { variant: string; color: number } | undefined {
    if (slot !== "h2") return this.opts.selection[slot];
    const h1 = this.opts.selection.h1;
    if (!h1 || h1.variant === "00") return undefined;
    const h2 = this.gender.slots.find((s) => s.slot === "h2");
    return h2?.variants.some((v) => v.variant === h1.variant) ? h1 : undefined;
  }

  /** Build the asset path for one slot's variant/colour at a given pose+dir+frame. */
  private slotPath(slot: string, variant: string, color: number, pose: string, dir: number, frame: number): string {
    const g = this.opts.gender;
    if (slot === "sh") return `characters/${slotCategory("sh")}/${g}_sh_01_c01_n01_01.png`;
    const d = String(dir).padStart(2, "0");
    const fr = String(frame).padStart(2, "0");
    const c = String(color).padStart(2, "0");
    return `characters/${slotCategory(slot)}/${g}_${slot}_${variant}_c${c}_${pose}${d}_${fr}.png`;
  }

  private path(s: SlotSprite, dir: number): string {
    if (s.slot === "sh") return this.slotPath("sh", "01", 1, "n", dir, 1);
    // Dueling is a STATIC held pose: every layer holds the LAST frame of its sequence
    // (body raised, disk fully up), no animation. The dd disk uses its single chibi
    // art (dd_01/c01) regardless of the worn bust disk SKU.
    if (this.duelingState) {
      const [pose, frame] = s.seq[s.seq.length - 1] ?? ["d", 1];
      if (s.slot === "dd") return this.slotPath("dd", "01", 1, pose, dir, frame);
      const dsel = this.effectiveSel(s.slot);
      return this.slotPath(s.slot, dsel?.variant ?? "01", dsel?.color ?? 1, pose, dir, frame);
    }
    const sel = this.effectiveSel(s.slot)!;
    // The slot's own frame sequence (from the .txt) decides its pose+frame for
    // this animation step, so the body cycles its 4 walk frames while hair/face
    // hold their idle frame (no 404 vanishing, no drift).
    const step = s.seq[(this.frame - 1) % s.seq.length] ?? ["n", 1];
    const [pose, frame] = step;
    return this.slotPath(s.slot, sel.variant, sel.color, pose, dir, frame);
  }

  /** Warm the texture cache for every pose/frame/direction this look can show, so the
   *  idle↔walk swap is instant instead of leaving the avatar in its old pose/look for the
   *  frames the new textures take to fetch, the "slides before the walk starts" glitch,
   *  and "walks without the new clothes" right after a customizer change. Fire-and-forget;
   *  cached loads make the next rebuild() resolve immediately. */
  async prewarm(): Promise<void> {
    const urls = new Set<string>();
    for (const pose of ["n", "w"]) {
      const byDir = this.layouts[pose] ?? this.layouts["n"] ?? {};
      for (const dir of [1, 2, 3, 4, 5]) { // all 5 drawn dirs; the right trio mirrors 2/3/4 (same art)
        for (const entry of this.withCostume(byDir[dir] ?? byDir[1] ?? [])) {
          const sel = this.opts.selection[entry.slot];
          if (entry.slot !== "sh" && (!sel || sel.variant === "00")) continue;
          for (const [p, fr] of entry.seq ?? [["n", 1]])
            urls.add(asset(this.slotPath(entry.slot, sel?.variant ?? "01", sel?.color ?? 1, p, dir, fr)));
        }
      }
    }
    await Promise.all([...urls].map((u) => Assets.load(u).catch(() => {})));
  }

  private async applyTexture(s: SlotSprite, dir: number): Promise<void> {
    try {
      s.sprite.texture = (await Assets.load(asset(this.path(s, dir)))) as Texture;
      s.sprite.visible = true;
    } catch {
      s.sprite.visible = false;
    }
  }

  setFacing(f: Facing): void {
    if (f === this.facing) return;
    this.facing = f;
    void this.rebuild(); // layout (order + offsets) differs per direction
  }

  /** Swap the rendered look (gender + slot selection) without recreating the sprite. Used after
   *  AvatarSet → Spawn echo so the local player's chibi reflects a saved customizer change without
   *  needing a refresh. Forces a rebuild so the new slot textures load. */
  setLook(gender: "cm" | "cw", selection: ChibiSelection): void {
    this.opts.gender = gender;
    this.opts.selection = selection;
    this.gender = this.opts.manifest.genders[gender]!;
    this.layouts = (this.opts.manifest.layouts?.[gender] ?? {}) as typeof this.layouts;
    this.layoutKey = ""; // force a layout reset; rebuild() picks up the new slots
    void this.rebuild();
    void this.prewarm(); // prime the new look's walk frames so the first step doesn't show the old clothes
  }

  /** Advance the walk cycle; rebuild when the pose (idle↔walk↔duel) changes. The
   *  duel pose is static (held), so only walking animates. */
  tick(dtMs: number): void {
    const dir = this.curDir;
    const wantKey = `${this.pose}:${dir}`;
    if (wantKey !== this.layoutKey) {
      this.frame = 1;
      void this.rebuild();
      return;
    }
    if (!this.walking || this.cycleLen <= 1) {
      if (this.frame !== 1) {
        this.frame = 1;
        void this.refresh(dir);
      }
      return;
    }
    this.animTimer += dtMs;
    if (this.animTimer >= 140) {
      this.animTimer = 0;
      this.frame = (this.frame % this.cycleLen) + 1;
      void this.refresh(dir);
    }
  }

  private async refresh(dir: number): Promise<void> {
    await Promise.all(this.slots.map((s) => this.applyTexture(s, dir)));
  }
}
